/**
 * The keys a shadow session adds to the store, and the one way to end it.
 *
 * Lives in its own module because both the request layer and AuthUtils need
 * it, and importing one from the other would close a cycle.
 *
 * The store updater is registered once by UserContext. The request layer has
 * no React context — it is a plain function called from everywhere — but it is
 * where an expired shadow token first shows up, as a 401 on whatever the admin
 * happened to click. Without a way back into the store its only option was to
 * send them to the login screen, which is wrong: their own token is sitting
 * right there, still valid.
 */

// eslint-disable-next-line no-unused-vars
type StoreUpdater = (next: any) => void;

let updateStoreFn: StoreUpdater | null = null;

/** Called once by UserContext so non-React code can end a shadow session. */
export function registerStoreUpdater(updater: StoreUpdater) {
  updateStoreFn = updater;
}

/** Every key a shadow session adds, cleared together so none outlives it. */
export function withoutShadow(store: any) {
  const cleared = { ...store };
  if (store?.admin_access_token) {
    cleared.access_token = store.admin_access_token;
  }
  delete cleared.admin_access_token;
  delete cleared.shadow_expires_at;
  delete cleared.impersonatingUser;
  return cleared;
}

export function isShadowing(store: any) {
  return !!store?.admin_access_token;
}

/**
 * End a shadow session from outside React, putting the admin's token back.
 *
 * Returns the restored store, or null when there was no shadow session to end
 * or nothing has registered an updater yet — so a caller can tell "recovered"
 * from "this really is a dead session".
 */
export function endShadowSession(store: any) {
  if (!isShadowing(store) || !updateStoreFn) return null;
  const restored = withoutShadow(store);
  updateStoreFn(restored);
  return restored;
}
